import { useQuery } from '@tanstack/react-query'

import { api, type MaybeEnveloped } from '@/lib/api-client'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { EffectChip } from './effect-chip'

/* Ecommerce vocabulary pickers (ECOM-DATA-GAPS G2b). One source of truth:
   GET /products/ecom-vocab — closed lists so storefront sections (mood grid,
   terpene filter, badges) behave identically across merchants. */

export interface EcomVocab {
  effects: string[]
  terpenes: string[]
  badges: string[]
}

export function useEcomVocab() {
  return useQuery({
    queryKey: queryKeys.ecomVocab,
    queryFn: async (): Promise<EcomVocab> => {
      const res = await api.get<MaybeEnveloped<EcomVocab>>('/products/ecom-vocab')
      const data = res as { data?: EcomVocab } & EcomVocab
      return data?.data ?? data
    },
    staleTime: Infinity,
  })
}

const BADGE_LABELS: Record<string, string> = {
  STAFF_PICK: 'Staff pick', NEW: 'New', SMALL_BATCH: 'Small batch',
  HOUSE_FAVORITE: 'House favorite', LIMITED: 'Limited',
}

export { effectLabel } from '../effect-vocab'

function ChipRow({ options, selected, onToggle, labelFor, max, renderEffect }: {
  options: string[]
  selected: string[]
  onToggle: (value: string) => void
  labelFor?: (v: string) => string
  max?: number
  /** Effects render as icon chips; other vocabularies stay plain pills. */
  renderEffect?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.includes(opt)
        const atCap = !active && max != null && selected.length >= max
        return (
          <button
            key={opt}
            type="button"
            disabled={atCap}
            onClick={() => onToggle(opt)}
            aria-pressed={active}
            className={cn(
              'rounded-full transition-opacity',
              !renderEffect && 'px-2.5 py-1 border text-xs transition-colors',
              !renderEffect &&
                (active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary/60'),
              renderEffect && !active && 'opacity-60 hover:opacity-100',
              atCap && 'opacity-40 cursor-not-allowed',
            )}
          >
            {renderEffect ? (
              <EffectChip value={opt} active={active} />
            ) : labelFor ? (
              labelFor(opt)
            ) : (
              opt
            )}
          </button>
        )
      })}
    </div>
  )
}

const toggle = (arr: string[], v: string) =>
  arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]

/** Effects ("feelings") chips — capped at 4 so cards stay scannable. */
export function EffectsPicker({ value, onChange }: {
  value: string[]
  onChange: (next: string[]) => void
}) {
  const vocab = useEcomVocab()
  if (!vocab.data) return null
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">
        Effects <span className="font-normal">(how it feels — up to 4)</span>
      </p>
      <ChipRow
        options={vocab.data.effects}
        selected={value}
        onToggle={(v) => onChange(toggle(value, v))}
        renderEffect
        max={4}
      />
    </div>
  )
}

export function TerpenesPicker({ value, onChange }: {
  value: string[]
  onChange: (next: string[]) => void
}) {
  const vocab = useEcomVocab()
  if (!vocab.data) return null
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">Dominant terpenes</p>
      <ChipRow
        options={vocab.data.terpenes}
        selected={value}
        onToggle={(v) => onChange(toggle(value, v))}
      />
    </div>
  )
}

/** Product badge — single-select from the curated set; click again to clear. */
export function BadgeSelect({ value, onChange }: {
  value: string | null
  onChange: (next: string | null) => void
}) {
  const vocab = useEcomVocab()
  if (!vocab.data) return null
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">Storefront badge</p>
      <ChipRow
        options={vocab.data.badges}
        selected={value ? [value] : []}
        onToggle={(v) => onChange(value === v ? null : v)}
        labelFor={(v) => BADGE_LABELS[v] ?? v}
      />
    </div>
  )
}
